import React, { useRef } from "react";
import { useSelector } from "react-redux";
import AccountLogo from "components/app-name/AccountLogo";
import { NumberFormatter } from "../../../../utils/index";
import DownloadButton from "components/button/download";
import FinalPayProcessButton from "./FinalPayProcessButton";
import Button from "components/button";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Card from "components/card";

const FinalPayComputation = () => {

	const recordData = useSelector((state) =>state.finalPay.finalPayDetails?.data);
	const finalPayRef = useRef();
	const handleDownloadPdf = async () => {
		if (!finalPayRef.current) return;

		document.body.classList.add("pdf-mode");

		try {
			const pdf = new jsPDF("p", "mm", "a4", true);

			const pageWidth = pdf.internal.pageSize.getWidth();
			const pageHeight = pdf.internal.pageSize.getHeight();

			const margin = 10;
			const usableWidth = pageWidth - margin * 2;
			const usableHeight = pageHeight - margin * 2;

			const pages = document.querySelectorAll(".pdf-page");

			let pageNumber = 0;

			for (let i = 0; i < pages.length; i++) {
				const canvas = await html2canvas(pages[i], {
					scale: 1.5,
					useCORS: true,
					backgroundColor: "#ffffff",
				});

				let renderedHeight = 0;

				const pageHeightPx = (canvas.width * usableHeight) / usableWidth;

				while (renderedHeight < canvas.height) {
					const sliceHeight = Math.min(pageHeightPx, canvas.height - renderedHeight);

					const pageCanvas = document.createElement("canvas");
					const ctx = pageCanvas.getContext("2d");

					pageCanvas.width = canvas.width;
					pageCanvas.height = sliceHeight;

					ctx.drawImage(
						canvas,
						0,
						renderedHeight,
						canvas.width,
						sliceHeight,
						0,
						0,
						canvas.width,
						sliceHeight
					);

					const imgData = pageCanvas.toDataURL("image/jpeg", 1.0);

					if (pageNumber > 0) pdf.addPage();

					const imgHeight = (sliceHeight * usableWidth) / canvas.width;

					pdf.addImage(
					imgData,
					"JPEG",
					margin,
					margin,
					usableWidth,
					Math.min(imgHeight, usableHeight),
					undefined,
					"FAST"
					);

					renderedHeight += sliceHeight;
					pageNumber++;
				}
			}

			pdf.save(`${recordData?.employee?.emp_code}_${recordData?.employee?.emp_name} - FP COMPUTATION.pdf`);
		} finally {
			document.body.classList.remove("pdf-mode");
		}
	};
	
	const lastPayComputation = [
		{title: "Basic Pay", taxable:"fpa_basic_pay", nontaxable: "_blank"},
		{title: "Tardiness/Undertime/Absence", taxable:"fpa_alu", nontaxable: "_blank"},
		{title: "De Minimis Allowance", taxable:"_blank", nontaxable: "fpa_de_minimis"},
		{title: "Service Allowance", taxable:"fpa_service_allowance_t", nontaxable: "fpa_service_allowance_nt"},
		{title: "Special Allowance", taxable:"fpa_special_allowance_t", nontaxable: "fpa_special_allowance_nt"},
		{title: "Night Differential", taxable:"fpa_night_differential", nontaxable: "_blank"},
		{title: "Overtime Pay", taxable:"fpa_overtime", nontaxable: "_blank"},
		{title: "Holiday Pay", taxable:"fpa_holiday_pay", nontaxable: "_blank"},
		{title: "Incentive / Bonus", taxable:"_blank", nontaxable: "fpa_incentives_bonus"},
		{title: "Other Earnings", taxable:"fpa_other_earnings_t", nontaxable: "fpa_other_earnings_nt"},
	];

	const taxComputation = [
		{title: "Total Gross Earnings", class: "font-bold text-right", field: "gross_compensation"},
		{title: "Tax Computation Details:", class: "font-bold", field: "_blank"},
		{title: "13th  Month Pay & Other Benefits", field: "13th_month_exempt"},
		{title: "De Minimis Benefits", field: "de_minimis"},
		{title: "Statutory Contributions", field: "statutory"},
		{title: "Other form of Non taxable Income", field: "other_compensations"},
		{title: "Total Non- Taxable", class: "font-bold text-right mb-2", field: "total_exempt"},
		{title: "Basic Salary", field: "basic_salary_taxable"},
		{title: "13th Month Pay & Other Benefits (In Excess of Threshold)", field: "13th_month_taxable"},
		{title: "Overtime", field: "overtime_pay"},
		{title: "Night Diffrential ", field: "night_differential"},
		{title: "Other Taxable Earnings", field: "other_taxable"},
		{title: "Total Taxable", class: "font-bold text-right", field: "taxable_compensation"},
	];
  
  	return (
		<>
		<Card extra={"w-full p-4"}>
			<div ref={finalPayRef}  className="pdf-page bg-white w-full h-full py-8 px-10 text-[12px]">
				<div className="flex justify-start items-center mb-2 border border-brand-600 rounded">
					<div className="p-4 ml-5">
						<AccountLogo accountId={3}/>
					</div>
					<div className="flex w-full justify-center pr-[100px]">
						<h2 className="font-bold text-[18px] uppercase">Final Pay Computation</h2>
					</div>
				</div>
				<div className="grid grid-cols-2 mt-2 p-4 border border-brand-600 rounded">
					<div className="grid grid-cols-3 font-medium item-start">
						<div className="font-bold dark:text-white">Employee ID</div>
						<div className="col-span-2 font-medium ps-3 dark:text-white">{recordData?.employee?.emp_code}</div>

						<div className="font-bold dark:text-white">Employee Name</div>
						<div className="col-span-2 font-medium ps-3 dark:text-white">{recordData?.employee?.emp_name}</div>

						<div className="font-bold dark:text-white">Job Position</div>
						<div className="col-span-2 font-medium ps-3 dark:text-white">{recordData?.employee?.emp_title}</div>
					</div>

					<div className="grid grid-cols-3 font-medium">
						<div className="font-bold dark:text-white">Hire Date</div>
						<div className="col-span-2 font-medium ps-3 dark:text-white">{recordData?.employee?.emp_hire_date}</div>

						<div className="font-bold dark:text-white">Separation Date</div>
						<div className="col-span-2 font-medium ps-3 dark:text-white">{recordData?.employee?.emp_separation_date}</div>

						<div className="font-bold dark:text-white">Department</div>
						<div className="col-span-2 font-medium ps-3 dark:text-white">{recordData?.employee?.emp_department_name}</div>
					</div>
				</div>

				<div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-2">
					<div className="box">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 justify-between ">
							<div className="font-black">
								Monthly Base Pay
							</div>
							<div className="text-right pe-2">
								{NumberFormatter(recordData?.fpa_salary)}
							</div>
						</div>
					</div> 
					<div className="box">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 justify-between">
							<div className="font-black">Daily Rate</div>
							<div className="text-right pe-2">{NumberFormatter(recordData?.fpa_daily_rate)}</div>
						</div>
					</div> 
				</div>

				<div className="grid grid-cols-1 mt-2 md:grid-cols-4 gap-0 md:gap-2">
					<div className="font-black uppercase box gray-1">
						Last Pay Computation
					</div> 
					<div className="font-black uppercase text-right box gray-1">
						Taxable
					</div>
					<div className="font-black uppercase text-right box gray-1">
						Non-Taxable
					</div>
					<div className="font-black uppercase text-right box gray-1">
						Total
					</div>
				</div>

				{lastPayComputation.map((item) => (
				<div className="grid grid-cols-1 md:grid-cols-4 gap-0 md:gap-2" key={item.title}>
					<div className="flex flex-col gap-2 px-2">
						<div className="">{item.title}</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.[item.taxable], 2, '-')}</div>
					</div>
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.[item.nontaxable], 2,'-')}</div>
					</div>
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter((recordData?.[item.nontaxable] ?? 0) + (recordData?.[item.taxable] ?? 0), 2,'-')}</div>
					</div>
				</div>
				))}
				<div className="grid grid-cols-1 mt-2 px-2 md:grid-cols-4 gap-0 md:gap-2 box gray-2">
					<div className="flex flex-col col-span-3 gap-2 px-2">
						<div className="font-black text-right uppercase">Total Amount</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_last_pay_amount)}</div>
					</div>
				</div>

				<div className="grid grid-cols-1 mt-2 md:grid-cols-4 gap-0 md:gap-2">
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black uppercase">13th Month Pay Computation</div>
					</div> 
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Base Pay</div>
					</div>
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Tardy / Undertime / Absence</div>
					</div>
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">13th Month Pay Base</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 md:grid-cols-4 gap-0 md:gap-2">
					<div className="flex flex-col gap-2 px-2">
						<div className="">{recordData?.fpa_13th_date_range}</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_13th_base_pay, 2, '-')}</div>
					</div>
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_13th_alu, 2,'-')}</div>
					</div>
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_13th_base_pay + recordData?.fpa_13th_alu, 2,'-')}</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box gray-2">
					<div className="flex flex-col col-span-3 gap-2 px-2">
						<div className="font-black text-right uppercase">13th Month Amount</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_13th_amount, 2,'-')}</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-2 md:grid-cols-4 gap-0 md:gap-2">
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black uppercase">Leave Conversion</div>
					</div> 
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Accrued (Hours)</div>
					</div>
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Used (Hours)</div>
					</div>
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Conversion (Hours)</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 md:grid-cols-4 gap-0 md:gap-2">
					<div className="flex flex-col gap-2 px-2">
						<div className="">{`${recordData?.fpa_lc_date_from ?? ''} - ${recordData?.fpa_lc_date_to ?? ''}`}</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_leave_accrued, 2, '-')}</div>
					</div>
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_leave_used, 2,'-')}</div>
					</div>
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_leave_accrued - recordData?.fpa_leave_used, 2,'-')}</div>
					</div>
				</div>
				{recordData?.fpa_lc_date_from_nt && recordData?.fpa_lc_date_to_nt && (
				<div className="grid grid-cols-1 mt-1 md:grid-cols-4 gap-0 md:gap-2">
					<div className="flex flex-col gap-2 px-2">
						<div className="">{`${recordData?.fpa_lc_date_from_nt ?? ''} - ${recordData?.fpa_lc_date_to_nt ?? ''}`}</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_leave_accrued_nt, 2, '-')}</div>
					</div>
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_leave_used_nt, 2,'-')}</div>
					</div>
					<div className="flex flex-col gap-2 px-2">
						<div className="text-right">{NumberFormatter(recordData?.fpa_leave_accrued_nt - recordData?.fpa_leave_used_nt, 2,'-')}</div>
					</div>
				</div>
				)}
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box gray-2">
					<div className="flex flex-col col-span-3 gap-2 px-2">
						<div className="font-black text-right uppercase">Leave Conversion Amount (T)</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_lc_amount_t, 2,'-')}</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box gray-2">
					<div className="flex flex-col col-span-3 gap-2 px-2">
						<div className="font-black text-right uppercase">Leave Conversion Amount (NT)</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_lc_amount_nt, 2,'-')}</div>
					</div>
				</div>

				<div className="grid grid-cols-1 mt-2 md:grid-cols-4 gap-0 md:gap-2">
					<div className="flex flex-col gap-1 box gray-1 col-span-1 md:col-span-3">
						<div className="font-black uppercase">Tax Computation</div>
					</div> 
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">COMPENSATION INCOME</div>
					</div>
				</div>
				
				{taxComputation.map((item) => (
				<div className="grid grid-cols-1 md:grid-cols-4 gap-0 md:gap-2" key={item.title}>
					<div className="flex flex-col gap-2 px-2 col-span-1 md:col-span-3">
						<div className={item.class || ''}>{item.title}</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className={item.class || 'text-right'}>{item.field !== '_blank' ? NumberFormatter(recordData?.data2316?.[item.field], 2, '-') : ''}</div>
					</div>
				</div>
				))}

				<div className="grid grid-cols-1 mt-2 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-gray-50">
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black uppercase">Net Taxable Income </div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.data2316?.taxable_compensation, 2,'-')}</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box">
					<div className="flex flex-col gap-2 px-2">
						<div className="font-medium">Excess Income to be Taxed</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-medium text-right">-</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box">
					<div className="flex flex-col gap-2 px-2">
						<div className="font-medium">Tax Rate</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-medium text-right">0%</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box">
					<div className="flex flex-col gap-2 px-2">
						<div className="font-medium">Base Tax</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-medium text-right">-</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-gray-50">
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black">Total Tax Due</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_total_tax_due, 2,'-')}</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-gray-50">
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black">Total Tax Withheld</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_total_tax_withheld, 2,'-')}</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-gray-50">
					<div className="flex flex-col gap-2 px-2 col-span-3">
						<div className="font-black">Tax Due (Refund)</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_tax_due_or_refund, 2,'-')}</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-gray-50">
					<div className="flex flex-col gap-2 px-2 col-span-3">
						<div className="font-black uppercase">Gross Final Pay</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_gross_final_pay, 2,'-')}</div>
					</div>
				</div>
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-gray-50">
					<div className="flex flex-col gap-2 px-2 col-span-3">
						<div className="font-black uppercase">Severance Pay</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_severance_pay, 2,'-')}</div>
					</div>
				</div>

				<div className="grid grid-cols-1 mt-1 md:grid-cols-4 gap-0 md:gap-2">
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black uppercase">Other Deductions</div>
					</div> 
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Amount</div>
					</div>
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Payment</div>
					</div>
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Balance</div>
					</div>
				</div>
				{recordData?.other_deductions?.map((item) => (
					<div className="grid grid-cols-1 mt-1 md:grid-cols-4 gap-0 md:gap-2" key={item.sdd_id}>
						<div className="flex flex-col gap-2 px-2">
							<div className="">{item.sdd_other_specific ?? item.sdd_paycode_name}</div>
						</div> 
						<div className="flex flex-col gap-2 px-2">
							<div className="text-right">{NumberFormatter(item.sdd_total_amount, 2, '-')}</div>
						</div>
						<div className="flex flex-col gap-2 px-2">
							<div className="text-right">{NumberFormatter(item.sdd_total_payment, 2,'-')}</div>
						</div>
						<div className="flex flex-col gap-2 px-2">
							<div className="text-right">{NumberFormatter(item.sdd_total_amount - item.sdd_total_payment)}</div>
						</div>
					</div>
				))}
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box gray-2">
					<div className="flex flex-col gap-2 px-2 col-span-3">
						<div className="font-black uppercase text-right">Other Deductions Amount</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_total_other_deductions, 2,'-')}</div>
					</div>
				</div>

				<div className="grid grid-cols-1 mt-1 md:grid-cols-4 gap-0 md:gap-2">
					<div className="flex flex-col gap-1 box gray-1 col-span-1 md:col-span-3">
						<div className="font-black uppercase">Other Additions</div>
					</div> 
					<div className="flex flex-col gap-1 box gray-1">
						<div className="font-black text-right uppercase">Amount</div>
					</div>
				</div>

				{recordData?.other_additions?.map((item) => (
					<div className="grid grid-cols-1 mt-1 md:grid-cols-4 gap-0 md:gap-2" key={item.sdd_id}>
						<div className="flex flex-col gap-2 px-2 col-span-1 md:col-span-3">
							<div className="">{item.sdn_other_specific ?? item.sdn_paycode_name}</div>
						</div> 
						<div className="flex flex-col gap-2 px-2">
							<div className="text-right">{NumberFormatter(item.sdn_total_amount, 2, '-')}</div>
						</div>
					</div>
				))}
				<div className="grid grid-cols-1 mt-1 px-2 md:grid-cols-4 gap-0 md:gap-2 box gray-2">
					<div className="flex flex-col gap-2 px-2 col-span-3">
						<div className="font-black uppercase text-right">Other Addition Amount</div>
					</div> 
					<div className="flex flex-col gap-2 px-2">
						<div className="font-black text-right">{NumberFormatter(recordData?.fpa_total_other_additions, 2,'-')}</div>
					</div>
				</div>

				{recordData?.fpa_parent_id > 0 ?
					<>
						<div className="grid grid-cols-1 mt-2 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-red-200">
							<div className="flex flex-col gap-2 px-2 col-span-3">
								<div className="font-black uppercase">Released Final Pay Amount</div>
							</div> 
							<div className="flex flex-col gap-2 px-2">
								<div className="font-black text-right">{NumberFormatter(recordData?.fpa_released_amount, 2,'-')}</div>
							</div>
						</div>

						<div className="grid grid-cols-1 mt-2 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-blue-200">
							<div className="flex flex-col gap-2 px-2 col-span-3">
								<div className="font-black uppercase">New Final Pay Amount</div>
							</div> 
							<div className="flex flex-col gap-2 px-2">
								<div className="font-black text-right">{NumberFormatter(recordData?.fpa_amount, 2,'-')}</div>
							</div>
						</div>

						<div className="grid grid-cols-1 mt-2 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-brandLinear">
							<div className="flex flex-col gap-2 px-2 col-span-3">
								<div className="font-black uppercase">Amended Final Pay Amount</div>
							</div> 
							<div className="flex flex-col gap-2 px-2">
								<div className="font-black text-right">{NumberFormatter(recordData?.fpa_released_amount - recordData?.fpa_amount, 2,'-')}</div>
							</div>
						</div>
					</>
					: 
					<div className="grid grid-cols-1 mt-2 px-2 md:grid-cols-4 gap-0 md:gap-2 box bg-brandLinear">
						<div className="flex flex-col gap-2 px-2 col-span-3">
							<div className="font-black uppercase">Final Pay Amount</div>
						</div> 
						<div className="flex flex-col gap-2 px-2">
							<div className="font-black text-right">{NumberFormatter(recordData?.fpa_amount, 2,'-')}</div>
						</div>
					</div>
				}

				{ (recordData?.fpa_status_id === 9 || recordData?.fpa_status_id === 10) && 
				<div className="mt-16 mb-2 flex w-full justify-center items-top gap-8">
					<div className="font-bold">Conformed By:</div>
					<div className="w-[300px] border-t-2 border-black mt-4">
						<div className="text-center">{recordData?.employee?.emp_name}</div>
					</div>
				</div>
				}
					
			</div>
		</Card>
		
			<div className="flex flex-row-reverse text-right mt-4">
				<DownloadButton downloadPath={`final-pay-2316/${recordData?.fpa_id}`} title="Final Pay 2316"/>

				<a className="btn btn-secondary me-3" href={`/private/employee/coe/${recordData?.fpa_employee_id}`}>
					View COE
				</a>

				<a className="btn btn-secondary me-3" href={`/console/final-pay/quit-claim/${recordData?.fpa_employee_id}`}>
					View Quit Claim
				</a>

				{recordData?.fpa_status_id < 8 && 
					<FinalPayProcessButton 
						finalPayId={recordData?.fpa_id}
						process="for review"
						confirmationMessage="Confirming that this final pay is ready for review."
					>
						For Review
					</FinalPayProcessButton>						  
				}

				{(recordData?.fpa_status_id === 8 && recordData?.fpa_latest_validation === 'FOR REVIEW') && //recordData?.fpa_last_user_validate != user?.id) &&
					<FinalPayProcessButton 
						finalPayId={recordData?.fpa_id}
						process="for approval"
						confirmationMessage="Confirming that this final pay is ready for approval."
					>
						For Approval
					</FinalPayProcessButton>						  
				}

				{(recordData?.fpa_status_id === 8 && recordData?.fpa_latest_validation === 'FOR APPROVAL') &&
					<FinalPayProcessButton 
						finalPayId={recordData?.fpa_id}
						process="approved"
						confirmationMessage="Confirming that this final pay is now approved."
					>
						Approved
					</FinalPayProcessButton>						  
				}

				{recordData?.fpa_status_id === 9 && 
					<FinalPayProcessButton className="btn-danger"
						finalPayId={recordData?.fpa_id}
						process="release final pay"
						confirmationMessage="Confirming that this final pay is ready for release."
					>
						Release Final Pay
					</FinalPayProcessButton>						  
				}

				{recordData?.fpa_status_id === 10 && 
					<FinalPayProcessButton className="btn-danger"
						finalPayId={recordData?.fpa_id}
						process="for amendment"
						confirmationMessage="Confirming that this final pay is to be amended."
					>
						For Amendment
					</FinalPayProcessButton>						  
				}
			</div>

			{ (recordData?.fpa_status_id === 9 || recordData?.fpa_status_id === 10) && 
			<div className="flex flex-row-reverse text-right mt-4">
				<Button className="btn-primary me-3" onClick={handleDownloadPdf}>
					Download as PDF
				</Button>
			</div>
			}	
		</>
  	);
};

export default FinalPayComputation;
